
















































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { ValidationProvider } from "vee-validate";
import { AttributesHelper } from "../../lib/utils/AttributesHelper";

/**
 * This component is an advanced version of the HTML select tag that matches our style guidelines
 */
@Component({
  components: {
    ValidationProvider
  }
})
export default class AnxSelect extends Vue {
  /** This is the id of the select field. A unqiue id will be created if this attribute is unset */
  @Prop({ default: null }) id!: string | null;
  /** This is the name for the select input. The id will be used as fallback */
  @Prop({ default: null }) name!: string | null;
  /** This is the label that will be displayed as description for the select input field */
  @Prop({ default: "" })
  label!: string;
  /**
   * This are the options for the select input field.
   * The properry should be an array of objcets with the following two properties: <br>
   * **value**: the value of the options and the value who will be passed to the parent<br>
   * **text**: the text who will be show in the application as label/text<br>
   * You can add a default/optional placeholder like "please choose one option" with {value: null, text: "please choose one option"}
   * The value for a placeholder must be "null" (important for the validation)
   */
  @Prop({ required: true }) options!: Array<{ value: string; text: string }>;
  /** The width for the select field */
  @Prop({ default: "100%" }) width!: string;
  /** This is the value that can be used via **v-model** */
  @Prop({ default: null }) value!: string;
  /**
   * If this property is set, a default required validation will be applied
   */
  @Prop({ default: null }) validation!: boolean | null;
  /** The rules for the validation */
  @Prop({ default: null }) validationRules!: string | null;

  /**
   * Check if a validation rule is provided and return this validation rule
   * If validation parameter is provided, return the default validation rule
   * Otherwise return an empty string
   */
  private get cValidationRules() {
    if (this.validationRules !== null) return this.validationRules;

    if (this.validation !== null) return "excluded:null";

    return "";
  }

  private selected = this.getOptionFromValue(this.value).value;
  private selectedText = this.getOptionFromValue(this.value).text;
  private show = false;
  private error: string[] = [];
  private dynamicHeight = false;
  private inputId: string | undefined = "";

  get cssProps() {
    return {
      "--select-width": this.width
    };
  }

  /**
   * Watch the selected variable. When selected is changed, the new value will be verify.
   * option "immediate:true" means, it will be execute before mounted
   * (this is important, for the validation observer)
   */
  @Watch("selected", { immediate: false })
  async onSelectedChanged(val: string) {
    if (val && this.validation !== null) {
      await this.verify(val);
    }

    window.setTimeout(this.handleResize, 100);
  }

  /**
   * Verfiy the selected value and generate the error-message for the custom select.
   */
  private async verify(value: string) {
    const { errors } = await this.$validator.verify(
      value,
      this.cValidationRules,
      {
        name: this.label
      }
    );
    this.error = errors;
  }

  /**
   * Set the id for the component when created
   */
  private created() {
    this.inputId = AttributesHelper.attributes(this, {
      uniqueId: true,
      uniqueIdPrefix: "anx-select-"
    }).id;
  }

  /**
   * Reset the error, because after mount it shouldn't show the error message
   */
  public mounted() {
    this.error = [];

    this.closeOnClickOutsideElement();
    window.setTimeout(this.handleResize, 100);
  }

  // Interface for ref
  $refs!: {
    anxSelect: HTMLFormElement;
  };

  /** Adapt the multiline select to the design */
  public handleResize() {
    this.dynamicHeight = this.$refs.anxSelect.clientHeight >= 38;
  }

  /** Close the select dialoag when the users clicks outside of the anx-select */
  private closeOnClickOutsideElement() {
    /** Add click event listener to the whole document */
    document.body.addEventListener("click", event => {
      /** This is the element, the user clicked */
      const clickedElement = event.target as Node;

      /** Check if the user clicked outside of the anx-select and close it */
      if (clickedElement) {
        if (!this.isElementOrChild(this.$el, clickedElement)) {
          this.closeDialog();
        }
      }
    });
  }

  /** Handle a click outside the modal */
  private closeDialog() {
    this.show = false;
  }

  /** Check if child matches elem or child is a child of elem */
  private isElementOrChild(elem: Node, child: Node) {
    return child === elem || elem.contains(child);
  }

  /**
   * Set the variable with the selcted option and emit the input-event
   */
  public select(option: { value: string; text: string }) {
    this.selected = option.value;
    this.selectedText = option.text;
    this.show = false;

    /** This emits the input event with the selected user input */
    this.$emit("input", this.selected);
  }

  /**
   * Returns the options object by providing a value
   * If no options is found, the first options in the array is returned
   */
  private getOptionFromValue(value: string): { value: string; text: string } {
    for (let i = this.options.length - 1; i > 0; i--) {
      if (this.options[i].value === value) {
        return this.options[i];
      }
    }

    return this.options[0];
  }
}
